import { useState } from "react";
import type { MonitorPlatform } from "../../../../shared/types";

const ALL_PLATFORMS: { key: MonitorPlatform; label: string }[] = [
  { key: "reddit", label: "Reddit" },
  { key: "twitter", label: "Twitter/X" },
  { key: "hackernews", label: "Hacker News" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "quora", label: "Quora" },
  { key: "indiehackers", label: "Indie Hackers" },
  { key: "producthunt", label: "Product Hunt" },
];

interface Props {
  brandId: string;
  onSubmit: (config: {
    brand_id: string;
    platforms: MonitorPlatform[];
    keywords: string[];
    competitor_names: string[];
  }) => Promise<void>;
  isCreating: boolean;
}

export function MonitorSetup({ brandId, onSubmit, isCreating }: Props) {
  const [platforms, setPlatforms] = useState<MonitorPlatform[]>(["reddit", "twitter", "hackernews"]);
  const [keywordsText, setKeywordsText] = useState("");
  const [competitorsText, setCompetitorsText] = useState("");

  const togglePlatform = (p: MonitorPlatform) => {
    setPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    );
  };

  const handleSubmit = async () => {
    const keywords = keywordsText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const competitor_names = competitorsText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    await onSubmit({
      brand_id: brandId,
      platforms,
      keywords,
      competitor_names,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-1 text-sm font-semibold text-slate-900">Set up Conversation Monitor</h3>
        <p className="text-xs text-slate-400">
          Discover conversations across the internet where your brand can naturally participate.
        </p>
      </div>

      {/* Platforms */}
      <div>
        <label className="mb-2 block text-xs font-medium text-slate-500">Platforms to scan</label>
        <div className="flex flex-wrap gap-2">
          {ALL_PLATFORMS.map((p) => (
            <button
              key={p.key}
              onClick={() => togglePlatform(p.key)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                platforms.includes(p.key)
                  ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:text-slate-600"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Keywords */}
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">
          Keywords <span className="text-slate-300">(comma-separated, optional)</span>
        </label>
        <input
          type="text"
          value={keywordsText}
          onChange={(e) => setKeywordsText(e.target.value)}
          placeholder="e.g. claude code mobile, remote development, AI coding tool"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-300 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
        />
        <p className="mt-1 text-xs text-slate-300">
          Leave blank to auto-generate from your brand research.
        </p>
      </div>

      {/* Competitors */}
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">
          Competitors to track <span className="text-slate-300">(comma-separated, optional)</span>
        </label>
        <input
          type="text"
          value={competitorsText}
          onChange={(e) => setCompetitorsText(e.target.value)}
          placeholder="e.g. Cursor, Copilot, Windsurf"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-300 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
        />
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={platforms.length === 0 || isCreating}
        className="w-full rounded-lg bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {isCreating ? "Creating..." : "Create Monitor"}
      </button>
    </div>
  );
}
